import { DestroyRef, Directive, ElementRef, HostListener, inject, Injector, Input, OnInit, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { NumberUtil } from '../util/NumberUtil';

const DATE_CHAR = /^[0-9.]$/;
const DATE_STRIP = /[^0-9.]/g;
const PHONE_CHAR = /^[0-9\s/()-]$/;
const PHONE_STRIP = /[^0-9\s/()-]/g;

function blockKey(event: KeyboardEvent, allowed: RegExp): void {
	if (event.ctrlKey || event.altKey || event.metaKey || event.key.length > 1) return;
	if (!allowed.test(event.key)) event.preventDefault();
}

function filterPaste(event: ClipboardEvent, el: HTMLInputElement, strip: RegExp): void {
	const text = event.clipboardData?.getData('text') ?? '';
	const filtered = text.replace(strip, '');
	if (filtered === text) return;
	event.preventDefault();
	const start = el.selectionStart ?? 0;
	const end = el.selectionEnd ?? 0;
	el.setRangeText(filtered, start, end, 'end');
	el.dispatchEvent(new Event('input', { bubbles: true }));
}

@Directive({
	selector: '[araDecimalFormat]',
	standalone: true,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: AraDecimalFormatDirective,
			multi: true,
		},
	],
})
export class AraDecimalFormatDirective implements ControlValueAccessor, OnInit {
	private readonly el = inject(ElementRef);
	private readonly renderer = inject(Renderer2);
	private readonly translocoLocaleService = inject(TranslocoLocaleService);
	private readonly numberUtil = inject(NumberUtil);
	private readonly destroyRef = inject(DestroyRef);

	private readonly deDE = 'de-DE';

	@Input() araInputMode: 'decimal' | 'phone' = 'decimal';
	@Input() allowNegative = true;
	@Input() decimalFractionDigitsMin = 2;
	@Input() decimalFractionDigitsMax = 2;
	@Input() minValue: number = undefined;
	@Input() maxValue: number = undefined;

	private get localeDecimalSeparator(): string {
		return this.translocoLocaleService.getLocale() === this.deDE ? ',' : '.';
	}

	private get localeThousandSeparator(): string {
		return this.translocoLocaleService.getLocale() === this.deDE ? '.' : ',';
	}

	private valueBeforeInput = '';
	private cachedValue: number | null = null;

	constructor() {
		if (this.decimalFractionDigitsMax < this.decimalFractionDigitsMin) {
			this.decimalFractionDigitsMin = this.decimalFractionDigitsMax;
		}
	}

	ngOnInit(): void {
		if (this.araInputMode !== 'decimal') return;
		this.translocoLocaleService.localeChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((locale) => {
			if (this.cachedValue === null) return;
			const formatted = new Intl.NumberFormat(locale, {
				minimumFractionDigits: this.decimalFractionDigitsMin,
				maximumFractionDigits: this.decimalFractionDigitsMax,
				useGrouping: false,
			}).format(this.cachedValue);
			this.renderer.setProperty(this.el.nativeElement, 'value', formatted);
		});
	}

	// ── ControlValueAccessor ─────────────────────────────────────────────────

	private onChange = (_value: any) => {};
	private onTouched = () => {};

	writeValue(value: any): void {
		if (this.araInputMode === 'phone') {
			this.renderer.setProperty(this.el.nativeElement, 'value', value ?? '');
			return;
		}
		this.cachedValue = value ?? null;
		const formattedValue = this.formatDecimal(value);
		this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue);
	}

	registerOnChange(fn: (value: any) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	// ── Event handlers ───────────────────────────────────────────────────────

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent): void {
		if (this.araInputMode === 'phone') {
			blockKey(event, PHONE_CHAR);
			return;
		}

		const preventedCharacters = [this.localeThousandSeparator];
		if (this.decimalFractionDigitsMax === 0) {
			preventedCharacters.push(this.localeDecimalSeparator);
		}
		if (preventedCharacters.includes(event.key)) {
			event.preventDefault();
		}
	}

	@HostListener('paste', ['$event'])
	onPaste(event: ClipboardEvent): void {
		if (this.araInputMode === 'phone') {
			filterPaste(event, this.el.nativeElement, PHONE_STRIP);
		}
	}

	@HostListener('beforeinput', ['$event'])
	onBeforeInput(event: any): void {
		if (this.araInputMode !== 'decimal') return;
		this.valueBeforeInput = event.target.value;
	}

	@HostListener('input', ['$event'])
	onInput(event: any): void {
		if (this.araInputMode === 'phone') {
			this.handlePhoneInput(event);
			return;
		}

		// ── decimal mode ─────────────────────────────────────────────────
		const value = event.target.value;
		let newCursorPosition = event.target.selectionStart;

		if (value.match(/^-/) && !this.allowNegative) {
			this.el.nativeElement.value = value.replace(/^-/, '');
			newCursorPosition--;
		} else if (value.match(/^(.+)-/)) {
			this.el.nativeElement.value = value.replace(/^(.+)-/, '$1');
			newCursorPosition--;
		} else if (value.match(/[^0-9.,-]/)) {
			this.el.nativeElement.value = value.replaceAll(/[^0-9.,-]/g, '');
			newCursorPosition--;
		} else {
			const splitValue = value.split(this.localeDecimalSeparator);
			const splitValueBeforeInput = this.valueBeforeInput.split(this.localeDecimalSeparator);
			if (splitValueBeforeInput.at(1)?.length < splitValue.at(1)?.length && splitValue.at(1)?.length > this.decimalFractionDigitsMax) {
				const cursorShift = event.data?.length ?? 0;
				const valueBeforeCursorPosition = value.substring(0, event.target.selectionStart - cursorShift);
				const valueAfterCursorPosition = value.substring(event.target.selectionStart - cursorShift);
				this.el.nativeElement.value =
					valueBeforeCursorPosition + valueAfterCursorPosition.substring(0, this.decimalFractionDigitsMax).replace(event.data, '');
				newCursorPosition = valueBeforeCursorPosition.length;
			}
		}

		if (this.el.nativeElement.selectionStart) {
			this.el.nativeElement.selectionStart = this.el.nativeElement.selectionEnd = newCursorPosition;
		}

		let numericValue = this.parseValue(this.el.nativeElement.value);
		if (!Number.isNaN(this.minValue) && numericValue < this.minValue) {
			numericValue = this.minValue;
			this.writeValue(numericValue);
		}
		if (!Number.isNaN(this.maxValue) && numericValue > this.maxValue) {
			numericValue = this.maxValue;
			this.writeValue(numericValue);
		}

		this.onChange(numericValue);
	}

	private handlePhoneInput(event: any): void {
		const value: string = event.target.value;
		const filtered = value.replaceAll(PHONE_STRIP, '');
		if (filtered !== value) {
			const cursor = Math.max(0, (event.target.selectionStart ?? filtered.length) - (value.length - filtered.length));
			this.el.nativeElement.value = filtered;
			this.el.nativeElement.setSelectionRange(cursor, cursor);
		}
		this.onChange(this.el.nativeElement.value);
	}

	@HostListener('blur', ['$event'])
	onBlur(event: any): void {
		this.onTouched();
		if (this.araInputMode !== 'decimal') return;
		const value = this.parseValue(event.target.value);
		this.writeValue(value);
	}

	// ── Private helpers ──────────────────────────────────────────────────────

	private parseValue(value: string): number {
		let cleaned = value;
		if (this.translocoLocaleService.getLocale() === this.deDE) {
			cleaned = cleaned
				.replaceAll(/[^\d.,-]/g, '')
				.replaceAll('.', '')
				.replace(',', '.');
		} else {
			cleaned = cleaned.replaceAll(/[^\d.,-]/g, '').replaceAll(',', '');
		}
		const parsed = Number.parseFloat(cleaned);
		return Number.isNaN(parsed) ? null : parsed;
	}

	private formatDecimal(value: number | null | undefined): string {
		if (value === null || value === undefined || Number.isNaN(value)) {
			return null;
		}
		return this.numberUtil.formatDecimal(value, this.decimalFractionDigitsMin, this.decimalFractionDigitsMax);
	}
}

/**
 * Filter-only directive for date inputs that already have their own CVA
 * (e.g. matDatepicker). Allows only digits and dots (German date format).
 * On blur, parses raw text (DDMMYYYY or DD.MM.YYYY) into a Date and pushes
 * it into the form control so that manual input actually works.
 */
@Directive({
	selector: '[araDateInput]',
	standalone: true,
})
export class AraDateInputDirective {
	private readonly el = inject(ElementRef<HTMLInputElement>);
	private readonly injector = inject(Injector);
	private rawValue = '';

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent): void {
		blockKey(event, DATE_CHAR);
	}

	@HostListener('paste', ['$event'])
	onPaste(event: ClipboardEvent): void {
		filterPaste(event, this.el.nativeElement, DATE_STRIP);
	}

	@HostListener('input')
	onInput(): void {
		this.rawValue = this.el.nativeElement.value;
	}

	@HostListener('blur')
	onBlur(): void {
		const raw = this.rawValue.trim();
		this.rawValue = '';

		const ngControl = this.injector.get(NgControl, null);
		if (!ngControl?.control) return;

		if (!raw) {
			if (ngControl.control.value instanceof Date) {
				ngControl.control.setValue(null);
			}
			return;
		}

		const date = parseDateDE(raw);
		if (date) {
			const current = ngControl.control.value;
			if (current instanceof Date && !Number.isNaN(current.getTime()) && current.getTime() === date.getTime()) return;
			ngControl.control.setValue(date);
		} else {
			ngControl.control.setValue(null);
			ngControl.control.setErrors({ matDatepickerParse: { text: raw } });
		}
	}
}

function parseDateDE(raw: string): Date | null {
	const digits = raw.replaceAll(/\D/g, '');

	if (digits.length === 8 && !raw.includes('.')) {
		return buildDate(
			Number.parseInt(digits.substring(4, 8), 10),
			Number.parseInt(digits.substring(2, 4), 10) - 1,
			Number.parseInt(digits.substring(0, 2), 10)
		);
	}

	const parts = raw.split('.');
	if (parts.length === 3 && parts[2].length >= 4) {
		return buildDate(Number.parseInt(parts[2], 10), Number.parseInt(parts[1], 10) - 1, Number.parseInt(parts[0], 10));
	}

	return null;
}

function buildDate(year: number, month: number, day: number): Date | null {
	if (year < 1900 || year > 2100) return null;
	const date = new Date(year, month, day);
	if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
	return date;
}
